import { NumberInputRootProps } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DiscountApplicationMethodEnum,
  ProductTypeEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { formatCurrency, roundNumberToDecimal } from '@monetize/utils/core';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ONE_TIME_PRODUCT_TYPES } from '../../constants/products';
import { calculateTargetAmount } from '../../utils/discounts';
import { getLongCodeForUnitPriceFrequency } from '../../utils/quotes';
import { MBox, MFlex, MFormLabel, MHStack, MStack } from './chakra';
import MRadio from './MRadio';
import MRadioGroup from './MRadioGroup';
import MButton from './MButton';
import MCenterModal from './MCenterModal';
import { MCurrencyInput } from './MCurrencyInput';
import MFormField from './MFormField';
import MText from './MText';
import { MTooltip } from './MTooltip';

export interface DiscountTargetPriceModalAmountOptions {
  amountWithoutDiscount: number;
  billingFrequencyInMonths: Maybe<number>;
  contractLength: number;
  currency: string;
  discountPercent: number;
  offeringPeriod: string;
  productName: string;
  targetAmount: number;
  productType: ProductTypeEnum;
  // This is the display price before discount
  displayUnitPrice: number;
  displayUnitPriceFrequency?: Maybe<number>;
  customDiscountAmountOrPercent?: Maybe<number>;
  catalogDiscountTotal: number;
  isUsageProduct?: boolean;
  discountApplicationMethod?: DiscountApplicationMethodEnum | null;
}

export interface DiscountTargetPriceModalProps extends NumberInputRootProps {
  targetAmountOptions: DiscountTargetPriceModalAmountOptions;
  isOpen: boolean;
  onClose: () => void;
  onChangeValue?: (value: number) => void;
}

export const SET_TARGET_VALUE_DISCOUNT_TYPE_OPTION = {
  title: 'Target Amount',
  value: 'SET_TARGET_VALUE',
};

export const DiscountSetTargetModalSchema = z.object({
  scope: z.enum(['UNIT_PRICE', 'TOTAL_AMOUNT']),
  unitPrice: z.union([z.string(), z.number()]),
  amount: z.union([z.string(), z.number()]),
});

export type DiscountSetTargetModal = z.infer<
  typeof DiscountSetTargetModalSchema
>;

const DiscountTargetPriceModal = React.forwardRef<
  HTMLDivElement,
  DiscountTargetPriceModalProps
>(
  (
    {
      targetAmountOptions: {
        amountWithoutDiscount,
        billingFrequencyInMonths,
        contractLength,
        currency,
        discountPercent,
        offeringPeriod,
        productName,
        targetAmount,
        productType,
        displayUnitPrice,
        displayUnitPriceFrequency,
        customDiscountAmountOrPercent,
        catalogDiscountTotal,
        isUsageProduct,
        discountApplicationMethod,
      },
      isOpen,
      onClose,
      onChangeValue,
    }: DiscountTargetPriceModalProps,
    ref: any,
  ) => {
    const isSequential =
      discountApplicationMethod === DiscountApplicationMethodEnum.SEQUENTIAL;
    const showTargetAmountWarning =
      contractLength >
      (displayUnitPriceFrequency || billingFrequencyInMonths || 0);

    const priceFrequency = useMemo(() => {
      return `per ${getLongCodeForUnitPriceFrequency(
        displayUnitPriceFrequency || billingFrequencyInMonths,
      )}`;
    }, [billingFrequencyInMonths, displayUnitPriceFrequency]);

    const amountAfterCatalogDiscount =
      catalogDiscountTotal > 0
        ? roundNumberToDecimal(
            amountWithoutDiscount * (1 - catalogDiscountTotal / 100),
            2,
          )
        : amountWithoutDiscount;

    const unitPriceAfterCatalogDiscount =
      catalogDiscountTotal > 0
        ? roundNumberToDecimal(
            displayUnitPrice * (1 - catalogDiscountTotal / 100),
            10,
          )
        : displayUnitPrice;

    const {
      handleSubmit,
      control,
      formState: { errors, isValid },
      reset,
      watch,
      setValue,
    } = useForm<DiscountSetTargetModal>({
      resolver: zodResolver(
        DiscountSetTargetModalSchema.superRefine((data, ctx) => {
          if (data.scope === 'TOTAL_AMOUNT') {
            const amountValue =
              typeof data.amount === 'string'
                ? parseFloat(data.amount)
                : data.amount;

            if (isNaN(amountValue)) {
              ctx.addIssue({
                code: 'custom',
                message: 'Enter a number',
                path: ['amount'],
              });
              return;
            }

            if (amountValue < 0) {
              ctx.addIssue({
                code: 'custom',
                message: 'Number must be greater than or equal to 0',
                path: ['amount'],
              });
            }

            if (amountValue > amountAfterCatalogDiscount) {
              ctx.addIssue({
                code: 'custom',
                message: `Target Amount must be less than or equal to discounted Amount of ${formatCurrency(
                  amountAfterCatalogDiscount,
                  { currency },
                )}`,
                path: ['amount'],
              });
            }

            if (amountValue > amountWithoutDiscount) {
              ctx.addIssue({
                code: 'custom',
                message: `Target Amount must be less than or equal to un-discounted Amount of ${formatCurrency(amountWithoutDiscount, { currency })}`,
                path: ['amount'],
              });
            }
          } else if (data.scope === 'UNIT_PRICE') {
            const unitPriceValue =
              typeof data.unitPrice === 'string'
                ? parseFloat(data.unitPrice)
                : data.unitPrice;

            if (isNaN(unitPriceValue)) {
              ctx.addIssue({
                code: 'custom',
                message: 'Enter a number',
                path: ['unitPrice'],
              });
              return;
            }

            if (unitPriceValue < 0) {
              ctx.addIssue({
                code: 'custom',
                message: 'Number must be greater than or equal to 0',
                path: ['unitPrice'],
              });
            }

            if (unitPriceValue > unitPriceAfterCatalogDiscount) {
              ctx.addIssue({
                code: 'custom',
                message: `Price Per Unit must be less than or equal to discounted unit price of ${formatCurrency(
                  unitPriceAfterCatalogDiscount,
                  { currency },
                )}`,
                path: ['unitPrice'],
              });
            }

            if (unitPriceValue > displayUnitPrice) {
              ctx.addIssue({
                code: 'custom',
                message: `Price Per Unit must be less than or equal to un-discounted Amount of ${formatCurrency(
                  displayUnitPrice,
                  { currency },
                )}`,
                path: ['unitPrice'],
              });
            }
          }
        }),
      ),
      mode: 'onChange',
      defaultValues: {
        scope: isUsageProduct ? 'UNIT_PRICE' : 'TOTAL_AMOUNT',
        amount: roundNumberToDecimal(targetAmount),
        unitPrice: roundNumberToDecimal(
          displayUnitPrice * (1 - discountPercent / 100),
          10,
        ),
      },
    });

    const [discount, setDiscount] = useState(0);
    const watchScope = watch('scope');

    useEffect(() => {
      if (isOpen) {
        reset({
          scope: 'TOTAL_AMOUNT',
          amount: roundNumberToDecimal(targetAmount),
          unitPrice: roundNumberToDecimal(
            displayUnitPrice * (1 - discountPercent / 100),
            10,
          ),
        });

        setDiscount(customDiscountAmountOrPercent || 0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, reset]);

    function calculateAmount(
      newValue: number,
      scope: DiscountSetTargetModal['scope'],
    ) {
      const hasCatalogDiscount = catalogDiscountTotal > 0;

      const { discount: newDiscount, mirroredValue } = calculateTargetAmount({
        newValue,
        scope,
        isSequential,
        hasCatalogDiscount,
        amountWithoutDiscount,
        amountAfterCatalogDiscount,
        displayUnitPrice,
        unitPriceAfterCatalogDiscount,
        catalogDiscountTotal,
      });

      if (scope === 'TOTAL_AMOUNT') {
        setValue('unitPrice', mirroredValue);
      } else {
        setValue('amount', mirroredValue);
      }

      setDiscount(newDiscount);
    }

    const onSubmit = async (requestData: DiscountSetTargetModal) => {
      try {
        onChangeValue && onChangeValue(discount);
        onClose();
      } catch {
        // handleApiErrorToast(err);
      }
    };

    if (!productName) {
      return null;
    }

    return (
      <MCenterModal
        size="lg"
        isOpen={isOpen}
        onClose={onClose}
        modalTitle={'Set Target Amount'}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        renderFooter={() => (
          <MStack
            gap={4}
            direction="row"
            align="center"
            justify="right"
            flex={1}
          >
            <MButton onClick={onClose} variant="cancel" minW="auto">
              Cancel
            </MButton>
            <MButton
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isDisabled={!isValid}
              type="submit"
              minW="auto"
            >
              Save
            </MButton>
          </MStack>
        )}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MFlex flexDir="column" gap={4}>
            <MText>
              Set the amount to be charged for {offeringPeriod}. <br /> The
              discount will be automatically calculated.
            </MText>
            <MBox>
              <MFormLabel>Product Name</MFormLabel>
              <MText>{productName}</MText>
            </MBox>
            {!isUsageProduct && (
              <Controller
                control={control}
                name="scope"
                render={({ field: { value, onChange, ...rest } }) => (
                  <MRadioGroup onChange={onChange} value={value} {...rest}>
                    <MStack gap={4} direction="column" color="tGray.darkPurple">
                      <MRadio value="TOTAL_AMOUNT">Target Total Amount</MRadio>
                      <MRadio value="UNIT_PRICE">Target Price Per Unit</MRadio>
                    </MStack>
                  </MRadioGroup>
                )}
              />
            )}
            <MHStack alignItems="flex-start" gap={6}>
              {watchScope === 'UNIT_PRICE' && (
                <MTooltip
                  isDisabled={catalogDiscountTotal <= 0}
                  placement="bottom-start"
                  label={`The upper limit for the PPU is the discounted PPU after
                    applying the existing discount of ${catalogDiscountTotal}%
                    which is ${formatCurrency(unitPriceAfterCatalogDiscount, {
                      currency,
                    })}`}
                >
                  <MFormField
                    label="Target Price Per Unit"
                    maxWidth={'170px'}
                    isInvalid={!!errors.unitPrice?.message}
                  >
                    <Controller
                      name="unitPrice"
                      control={control}
                      render={({ field: { onChange, ...rest } }) => (
                        <MCurrencyInput
                          precision={10}
                          inputMode="decimal"
                          onChange={(valueAsString, valueAsNumber) => {
                            if (valueAsNumber > targetAmount) {
                              setDiscount(0.0);
                            }
                            onChange(valueAsString);
                            calculateAmount(valueAsNumber, 'UNIT_PRICE');
                          }}
                          {...rest}
                        />
                      )}
                    />
                    {!ONE_TIME_PRODUCT_TYPES.has(productType) && (
                      <MText>{priceFrequency}</MText>
                    )}
                  </MFormField>
                </MTooltip>
              )}
              {watchScope === 'TOTAL_AMOUNT' && (
                <MTooltip
                  isDisabled={catalogDiscountTotal <= 0}
                  placement="bottom-start"
                  label={`The upper limit for the Target Amount is the discounted
                    Amount after applying the existing discount of ${catalogDiscountTotal}%
                    which is ${formatCurrency(amountAfterCatalogDiscount, {
                      currency,
                    })}`}
                >
                  <MFormField
                    label={
                      isUsageProduct ? 'Target Price Per Unit' : 'Target Amount'
                    }
                    maxWidth={'170px'}
                    isInvalid={!!errors.amount?.message}
                  >
                    <Controller
                      name="amount"
                      control={control}
                      render={({ field: { onChange, ...rest } }) => (
                        <MCurrencyInput
                          precision={2}
                          inputMode="decimal"
                          onChange={(
                            valueAsString: string,
                            valueAsNumber: number,
                          ) => {
                            if (valueAsNumber > targetAmount) {
                              setDiscount(0.0);
                            }
                            onChange(valueAsString);
                            calculateAmount(valueAsNumber, 'TOTAL_AMOUNT');
                          }}
                          {...rest}
                        />
                      )}
                    />
                  </MFormField>
                </MTooltip>
              )}
              <MFormField label="Custom Discount">
                <MText mt={1}>{roundNumberToDecimal(discount, 10)}%</MText>
              </MFormField>
              {catalogDiscountTotal > 0 && (
                <MFormField label="Total Discount">
                  <MText mt={1}>
                    {roundNumberToDecimal(
                      isSequential
                        ? (1 -
                            (1 - catalogDiscountTotal / 100) *
                              (1 - discount / 100)) *
                            100
                        : Math.min(catalogDiscountTotal + discount, 100),
                      10,
                    )}
                    %
                  </MText>
                </MFormField>
              )}
            </MHStack>
            <MBox minH="20px" mt={-2}>
              <MText color="tRed.base" fontSize="sm">
                {watchScope === 'TOTAL_AMOUNT' && errors?.amount?.message}
              </MText>
              <MText color="tRed.base" fontSize="sm">
                {watchScope === 'UNIT_PRICE' && errors?.unitPrice?.message}
              </MText>
            </MBox>
            {showTargetAmountWarning && (
              <MText color="tOrange.tangerine" fontSize="sm">
                Upon Save, the Target Amount may be adjusted slightly to create
                a billing schedule with equal payments.
              </MText>
            )}
          </MFlex>
        </form>
      </MCenterModal>
    );
  },
);

export default DiscountTargetPriceModal;
